import { Link } from "@tanstack/react-router";
import { Clock, ArrowRight } from "lucide-react";
import { publishedPosts, categoryColors, type BlogPost } from "@/data/blog-posts";

interface RelatedPostsProps {
  currentSlug: string;
  relatedSlugs: string[];
}

const RelatedPosts = ({ currentSlug, relatedSlugs }: RelatedPostsProps) => {
  const relatedPosts = relatedSlugs
    .map((slug) => publishedPosts.find((p) => p.slug === slug))
    .filter((p): p is BlogPost => !!p && p.slug !== currentSlug)
    .slice(0, 3);

  if (relatedPosts.length === 0) return null;

  return (
    <div className="mt-10 sm:mt-12 pt-8 sm:pt-10 border-t border-border/50">
      <h3 className="text-lg sm:text-2xl font-typewriter font-bold text-foreground mb-5 sm:mb-6">
        Continue lendo
      </h3>

      {/* Mobile: vertical stack | Desktop: grid */}
      <div className="flex flex-col gap-3 sm:grid sm:grid-cols-3">
        {relatedPosts.map((post) => (
          <Link
            key={post.slug}
            to="/blog/$slug" params={{ slug: post.slug }}
            className="group flex items-start gap-3 sm:flex-col sm:gap-0 rounded-xl bg-card border border-border/50 p-3 sm:p-4 hover:shadow-md hover:border-primary/20 transition-all active:scale-[0.98]"
          >
            {/* Mobile: compact row layout */}
            <div className="flex-1 min-w-0">
              <span className={`inline-block text-[0.65rem] sm:text-xs font-semibold px-2 py-0.5 rounded-full mb-1.5 sm:mb-2 ${categoryColors[post.category]}`}>
                {post.categoryLabel}
              </span>
              <h4 className="text-[0.8rem] sm:text-sm font-bold text-foreground group-hover:text-primary transition-colors leading-snug font-sans line-clamp-2">
                {post.title}
              </h4>
              <span className="flex items-center gap-1 text-[0.65rem] sm:text-xs text-muted-foreground mt-1.5 sm:mt-3">
                <Clock className="w-3 h-3" />
                {post.readTime}
              </span>
            </div>
            <ArrowRight className="w-4 h-4 text-primary flex-shrink-0 mt-1 sm:hidden" />
          </Link>
        ))}
      </div>
    </div>
  );
};

export default RelatedPosts;
